/**
 * Change Analysis — the Existing ↔ Proposed diff engine (Phase 9, spec §17A).
 *
 * Pure and deterministic: compares a BASELINE plan model (the project's
 * Existing design — or null for new construction, where every proposed
 * element is an addition) against a PROPOSED plan model and returns
 * ChangeFindings. No I/O, no persistence — reconciliation into scope is the
 * coordinator's job (src/db/designChangeCoordinator.ts).
 *
 * Entity matching uses ROOT LINEAGE (originId ?? id — Phase 8 guarantees
 * origin always points at the root, never a chain) plus geometry:
 *
 *  - Walls match on COVERED CENTERLINE INTERVALS per baseline wall, so a
 *    wall that was merely split by a new intersecting wall is NOT reported
 *    as demolished. Logical room boundaries are never physical construction
 *    and never produce findings.
 *  - Openings match by root within their host wall's root.
 *  - Objects match by root; Proposed-only objects are Phase 8's territory
 *    (auto-scope on placement) and are reported as informational only.
 *
 * Classification (spec §17A, settled): added/removed auto-apply when the
 * scope interpretation is unambiguous; moved/resized/modified/partial and
 * baseline-corrected-after-proposal findings are review-only.
 */
import type { PlanModel, PlanObject, Wall, WallOpening } from "./model";
import { PU_PER_FOOT, PU_PER_INCH } from "./units";

export type ChangeClassification = "added" | "removed" | "moved" | "resized" | "modified" | "unchanged" | "review";

export type ChangeEntityKind = "wall" | "opening" | "object";

export interface ChangeScopeIntent {
  /** Feature definition the finding's scope maps to. */
  featureDefinitionId: string;
  action: "install" | "remove";
  /** 'lf' quantities carry LF x100; 'each' carry counts x100. */
  unit: "lf" | "each";
}

export interface ChangeFinding {
  /** Stable across recomputation: `${entityKind}-${classification}:${rootId}`. */
  key: string;
  entityKind: ChangeEntityKind;
  classification: ChangeClassification;
  /** True when the scope interpretation is unambiguous (added/removed). */
  autoApply: boolean;
  /** Root entity id (baseline id for removals, proposed id for additions). */
  rootId: string;
  baselineIds: string[];
  proposedIds: string[];
  /** Quantity contribution (x100; LF for walls, count for the rest). */
  quantityX100: number | null;
  explanation: string;
  caution: string | null;
  /** Scope intent; null for informational / review-only findings. */
  scope: ChangeScopeIntent | null;
  /** Phase 8 already scoped it (Proposed-only object) — never re-scope. */
  informational: boolean;
  /**
   * HOST-WALL SUPERSESSION (§79.1): the removal half of this change is
   * included in the demolition of the wall that hosts the opening — a
   * moved opening whose old host wall is itself being removed must not
   * also bill "close the old opening". Derived, never stored.
   */
  supersededRemoval?: boolean;
}

export interface DiffOptions {
  /** Geometric tolerance in pu (default 1"): differences below it are noise, never construction. */
  tolerancePu?: number;
  /**
   * Baseline entity ids documented AFTER the proposed design was created
   * (Existing corrections): their "removals" are review-only, never
   * auto-demolition (spec §17A).
   */
  baselineCorrectedIds?: ReadonlySet<string>;
}

export const DEFAULT_DIFF_TOLERANCE_PU = PU_PER_INCH; // 1 inch

const lfX100 = (pu: number): number => Math.round((pu * 100) / PU_PER_FOOT);

interface Pt {
  x: number;
  y: number;
}

function wallEndpoints(m: PlanModel, w: Wall): [Pt, Pt] | null {
  const a = m.nodes.get(w.startNodeId);
  const b = m.nodes.get(w.endNodeId);
  if (!a || !b) return null;
  return [
    { x: a.x, y: a.y },
    { x: b.x, y: b.y },
  ];
}

const dist = (a: Pt, b: Pt): number => Math.hypot(a.x - b.x, a.y - b.y);

/** Union length of intervals, clipped to [0, len]. */
function coveredLength(intervals: Array<[number, number]>, len: number): number {
  const clipped = intervals
    .map(([s, e]): [number, number] => [Math.max(0, Math.min(s, e)), Math.min(len, Math.max(s, e))])
    .filter(([s, e]) => e > s)
    .sort((p, q) => p[0] - q[0]);
  let total = 0;
  let cursor = -Infinity;
  for (const [s, e] of clipped) {
    const start = Math.max(s, cursor);
    if (e > start) total += e - start;
    cursor = Math.max(cursor, e);
  }
  return total;
}

const rootOf = (e: { id: string; originId: string | null }): string => e.originId ?? e.id;

function activeWalls(m: PlanModel): Wall[] {
  return [...m.walls.values()].filter((w) => w.archivedAt === null && w.kind === "wall");
}

function activeOpenings(m: PlanModel): WallOpening[] {
  return [...m.openings.values()].filter((o) => {
    if (o.archivedAt !== null) return false;
    const host = m.walls.get(o.wallId);
    return host !== undefined && host.archivedAt === null;
  });
}

function activeObjects(m: PlanModel): PlanObject[] {
  return [...m.objects.values()].filter((o) => o.archivedAt === null);
}

/** Distance of point p from the infinite line through a with unit dir u. */
function offLine(p: Pt, a: Pt, u: Pt): number {
  return Math.abs((p.x - a.x) * -u.y + (p.y - a.y) * u.x);
}

export function diffPlans(baseline: PlanModel | null, proposed: PlanModel, options: DiffOptions = {}): ChangeFinding[] {
  const tol = options.tolerancePu ?? DEFAULT_DIFF_TOLERANCE_PU;
  const corrected = options.baselineCorrectedIds ?? new Set<string>();
  const findings: ChangeFinding[] = [];

  // ---------------- Walls ----------------
  const baseWalls = baseline ? activeWalls(baseline) : [];
  const propWalls = activeWalls(proposed);
  const baseWallRoots = new Set(baseWalls.map((w) => rootOf(w)));
  const propByRoot = new Map<string, Wall[]>();
  for (const w of propWalls) {
    const r = rootOf(w);
    const list = propByRoot.get(r) ?? [];
    list.push(w);
    propByRoot.set(r, list);
  }

  // Per-baseline-wall retained coverage along the wall line (t-ranges in
  // pu from the wall start). The opening pass reads this to decide whether
  // the wall material AT an opening survives in the proposed design (§79.1
  // host-wall supersession) — geometry and lineage, never labels.
  const wallCoverage = new Map<string, { len: number; intervals: Array<[number, number]> }>();
  for (const b of baseWalls) {
    if (!baseline) break;
    const ends = wallEndpoints(baseline, b);
    if (!ends) continue;
    const [a, z] = ends;
    const len = dist(a, z);
    if (len <= tol) continue;
    const u = { x: (z.x - a.x) / len, y: (z.y - a.y) / len };
    const root = rootOf(b);
    const segments = propByRoot.get(root) ?? [];
    const collinear: Array<[number, number]> = [];
    let displaced = 0;
    let thicknessChanged = false;
    for (const s of segments) {
      const se = wallEndpoints(proposed, s);
      if (!se) continue;
      const [p, q] = se;
      if (offLine(p, a, u) <= tol && offLine(q, a, u) <= tol) {
        const t1 = (p.x - a.x) * u.x + (p.y - a.y) * u.y;
        const t2 = (q.x - a.x) * u.x + (q.y - a.y) * u.y;
        collinear.push([t1, t2]);
        if (Math.abs(s.thickness - b.thickness) > tol / 2) thicknessChanged = true;
      } else {
        displaced++;
      }
    }
    wallCoverage.set(b.id, { len, intervals: collinear });
    const covered = coveredLength(collinear, len);
    const segmentIds = segments.map((s) => s.id);
    if (covered >= len - tol) {
      if (thicknessChanged) {
        findings.push({
          key: `wall-modified:${root}`,
          entityKind: "wall",
          classification: "modified",
          autoApply: false,
          rootId: root,
          baselineIds: [b.id],
          proposedIds: segmentIds,
          quantityX100: null,
          explanation: "This wall's thickness changed from Existing — review what construction that implies.",
          caution: null,
          scope: null,
          informational: false,
        });
      }
      continue; // unchanged — Keep is an analysis result, never scope
    }
    if (segments.length === 0) {
      if (corrected.has(b.id)) {
        findings.push({
          key: `wall-corrected:${root}`,
          entityKind: "wall",
          classification: "review",
          autoApply: false,
          rootId: root,
          baselineIds: [b.id],
          proposedIds: [],
          quantityX100: lfX100(len),
          explanation:
            "This wall was documented in Existing AFTER this proposed design was created. Confirm whether the proposal really removes it.",
          caution: null,
          scope: null,
          informational: false,
        });
      } else {
        findings.push({
          key: `wall-removed:${root}`,
          entityKind: "wall",
          classification: "removed",
          autoApply: true,
          rootId: root,
          baselineIds: [b.id],
          proposedIds: [],
          quantityX100: lfX100(len),
          explanation: `This wall exists in Existing but has no corresponding wall in the proposed design (${(lfX100(len) / 100).toFixed(2)} LF).`,
          caution: "Structural review may be required before removing any wall.",
          scope: { featureDefinitionId: "remove-interior-wall", action: "remove", unit: "lf" },
          informational: false,
        });
      }
      continue;
    }
    if (covered <= tol && displaced > 0) {
      findings.push({
        key: `wall-moved:${root}`,
        entityKind: "wall",
        classification: "moved",
        autoApply: false,
        rootId: root,
        baselineIds: [b.id],
        proposedIds: segmentIds,
        quantityX100: lfX100(len),
        explanation:
          "This wall was relocated from its Existing position. Relocation usually means removing the old wall AND building a new one — review and accept.",
        caution: "Structural review may be required before removing any wall.",
        scope: null,
        informational: false,
      });
      continue;
    }
    findings.push({
      key: `wall-partial:${root}`,
      entityKind: "wall",
      classification: "review",
      autoApply: false,
      rootId: root,
      baselineIds: [b.id],
      proposedIds: segmentIds,
      quantityX100: lfX100(Math.max(0, len - covered)),
      explanation: `Only part of this Existing wall remains in the proposed design (${((len - covered) / PU_PER_FOOT).toFixed(1)} ft is unaccounted for). The exact construction implication needs review.`,
      caution: "Structural review may be required before removing any wall.",
      scope: null,
      informational: false,
    });
  }

  // Added walls: proposed walls whose root has no baseline counterpart.
  for (const w of propWalls) {
    const root = rootOf(w);
    if (baseWallRoots.has(root)) continue;
    const ends = wallEndpoints(proposed, w);
    if (!ends) continue;
    const len = dist(ends[0], ends[1]);
    if (len <= tol) continue;
    findings.push({
      key: `wall-added:${w.id}`,
      entityKind: "wall",
      classification: "added",
      autoApply: true,
      rootId: w.id,
      baselineIds: [],
      proposedIds: [w.id],
      quantityX100: lfX100(len),
      explanation: baseline
        ? `This wall exists only in the proposed design (${(lfX100(len) / 100).toFixed(2)} LF) — it must be built.`
        : `New construction: this wall must be built (${(lfX100(len) / 100).toFixed(2)} LF).`,
      caution: null,
      scope: { featureDefinitionId: "build-interior-wall", action: "install", unit: "lf" },
      informational: false,
    });
  }

  // ---------------- Openings ----------------
  const baseOpenings = baseline ? activeOpenings(baseline) : [];
  const propOpenings = activeOpenings(proposed);
  const propOpeningByRoot = new Map(propOpenings.map((o) => [rootOf(o), o]));
  const baseOpeningRoots = new Set(baseOpenings.map((o) => rootOf(o)));
  /**
   * Does the wall material at this baseline opening survive in the
   * proposed design? False when the opening span lies in demolished wall
   * (whole wall removed, the removed part of a partial removal, or a
   * moved wall) — then the opening disappears WITH the wall and closing
   * it is not separate work (§79.1). A host wall the coverage pass never
   * measured (zero-length, missing endpoints) stays conservative: the
   * closure is kept billable rather than silently dropped.
   */
  const openingWallRetained = (o: WallOpening): boolean => {
    const cov = wallCoverage.get(o.wallId);
    if (!cov) return true;
    const s = o.offsetFromStart;
    const e = o.offsetFromStart + o.width;
    let covered = 0;
    for (const [t1, t2] of cov.intervals) {
      const lo = Math.max(Math.min(t1, t2), s);
      const hi = Math.min(Math.max(t1, t2), e);
      if (hi > lo) covered += hi - lo;
    }
    return covered >= e - s - tol;
  };
  const openingDefs = (kind: WallOpening["kind"], action: "install" | "remove"): ChangeScopeIntent | null => {
    if (kind === "door") return { featureDefinitionId: action === "install" ? "install-interior-door" : "remove-door-opening", action, unit: "each" };
    if (kind === "window") return { featureDefinitionId: action === "install" ? "install-window" : "remove-window-opening", action, unit: "each" };
    return null; // cased openings: conservative, review-only
  };
  const kindLabel = (k: WallOpening["kind"]): string => (k === "door" ? "door" : k === "window" ? "window" : "cased opening");

  for (const b of baseOpenings) {
    const root = rootOf(b);
    const p = propOpeningByRoot.get(root);
    if (p) {
      const resized = Math.abs(p.width - b.width) > tol || Math.abs(p.height - b.height) > tol || Math.abs(p.sillHeight - b.sillHeight) > tol;
      const hostRootB = baseline ? rootOf(baseline.walls.get(b.wallId) ?? { id: b.wallId, originId: null }) : b.wallId;
      const hostRootP = rootOf(proposed.walls.get(p.wallId) ?? { id: p.wallId, originId: null });
      const moved = hostRootB !== hostRootP || Math.abs(p.offsetFromStart - b.offsetFromStart) > tol;
      if (resized || moved) {
        const oldSpanDemolished = moved && !openingWallRetained(b);
        findings.push({
          key: `opening-${resized ? "resized" : "moved"}:${root}`,
          entityKind: "opening",
          classification: resized ? "resized" : "moved",
          autoApply: false,
          rootId: root,
          baselineIds: [b.id],
          proposedIds: [p.id],
          quantityX100: null,
          explanation: resized
            ? `This ${kindLabel(b.kind)} changed size from Existing — that may mean reframing the opening. Review the construction implication.`
            : oldSpanDemolished
              ? `This ${kindLabel(b.kind)} moved from its Existing position, and the wall it came out of is being removed — the new opening must be created, but there is no old opening to close. Review.`
              : `This ${kindLabel(b.kind)} moved from its Existing position — that may mean closing the old opening and creating a new one. Review.`,
          caution: null,
          scope: null,
          informational: false,
          supersededRemoval: oldSpanDemolished,
        });
      }
      continue;
    }
    if (corrected.has(b.id)) {
      findings.push({
        key: `opening-corrected:${root}`,
        entityKind: "opening",
        classification: "review",
        autoApply: false,
        rootId: root,
        baselineIds: [b.id],
        proposedIds: [],
        quantityX100: 100,
        explanation: `This ${kindLabel(b.kind)} was documented in Existing AFTER this proposed design was created. Confirm whether the proposal really removes it.`,
        caution: null,
        scope: null,
        informational: false,
      });
      continue;
    }
    if (!openingWallRetained(b)) {
      // §79.1: the wall that holds this opening is itself demolished (in
      // whole, or the demolished part of a partial removal). The opening
      // ceases to exist because THE WALL ceases to exist — there is
      // nothing to close, so no closure scope and no cost. The design
      // still knows the opening is gone; this is estimating semantics,
      // not design semantics.
      findings.push({
        key: `opening-removed:${root}`,
        entityKind: "opening",
        classification: "removed",
        autoApply: false,
        rootId: root,
        baselineIds: [b.id],
        proposedIds: [],
        quantityX100: 100,
        explanation: `This ${kindLabel(b.kind)} disappears with its wall — the wall demolition includes it, so there is no opening left to close and no separate cost.`,
        caution: null,
        scope: null,
        informational: true,
        supersededRemoval: true,
      });
      continue;
    }
    const scope = openingDefs(b.kind, "remove");
    findings.push({
      key: `opening-removed:${root}`,
      entityKind: "opening",
      classification: scope ? "removed" : "review",
      autoApply: scope !== null,
      rootId: root,
      baselineIds: [b.id],
      proposedIds: [],
      quantityX100: 100,
      explanation: `This ${kindLabel(b.kind)} exists in Existing but not in the proposed design — the opening must be removed/closed.`,
      caution: null,
      scope,
      informational: false,
    });
  }
  for (const p of propOpenings) {
    const root = rootOf(p);
    if (baseOpeningRoots.has(root)) continue;
    const scope = openingDefs(p.kind, "install");
    findings.push({
      key: `opening-added:${p.id}`,
      entityKind: "opening",
      classification: scope ? "added" : "review",
      autoApply: scope !== null,
      rootId: p.id,
      baselineIds: [],
      proposedIds: [p.id],
      quantityX100: 100,
      explanation: baseline
        ? `This ${kindLabel(p.kind)} exists only in the proposed design — it must be installed.`
        : `New construction: this ${kindLabel(p.kind)} must be installed.`,
      caution: null,
      scope,
      informational: false,
    });
  }

  // ---------------- Objects ----------------
  const baseObjects = baseline ? activeObjects(baseline) : [];
  const propObjects = activeObjects(proposed);
  const propObjectRoots = new Set(propObjects.map((o) => rootOf(o)));

  /*
   * REPLACEMENT PAIRING (§79.1). Taking the old toilet out the morning the
   * new one goes in is not a demolition project — it is the first hour of
   * the installation, and every replacement assembly already prices it
   * ("Remove & Dispose Old Toilet"). So a removed Existing object that is
   * answered by a NEW object of the SAME definition in the proposed design
   * generates no standalone removal cost.
   *
   * Pairing is by definition and by COUNT, so removing three and adding
   * one leaves two genuinely-gone objects still billable — §79.1 never
   * blindly zeroes a removal. Deterministic: both sides are ordered by id,
   * and pairs are taken in that order.
   */
  const newPropByDef = new Map<string, string[]>();
  for (const o of [...propObjects].sort((a, z) => (a.id < z.id ? -1 : 1))) {
    if (baseline && propObjectRoots.has(rootOf(o)) && baseObjects.some((b) => rootOf(b) === rootOf(o))) continue; // inherited copy, not new
    newPropByDef.set(o.definitionId, [...(newPropByDef.get(o.definitionId) ?? []), o.id]);
  }
  const replacedByInstall = new Map<string, string>();
  for (const b of [...baseObjects].sort((a, z) => (a.id < z.id ? -1 : 1))) {
    if (propObjectRoots.has(rootOf(b))) continue; // still there — Keep
    if (corrected.has(b.id)) continue;
    const left = newPropByDef.get(b.definitionId) ?? [];
    if (left.length > 0) {
      replacedByInstall.set(b.id, left.shift()!);
    }
  }

  for (const b of baseObjects) {
    const root = rootOf(b);
    if (propObjectRoots.has(root)) {
      const p = propObjects.find((o) => rootOf(o) === root)!;
      const movedFar = Math.hypot(p.x - b.x, p.y - b.y) > 12 * PU_PER_INCH; // 1 ft: real relocation, not a nudge
      if (movedFar && p.featureId === null) {
        findings.push({
          key: `object-moved:${root}`,
          entityKind: "object",
          classification: "moved",
          autoApply: false,
          rootId: root,
          baselineIds: [b.id],
          proposedIds: [p.id],
          quantityX100: null,
          explanation: "This fixture moved noticeably from its Existing position — relocation may imply plumbing/electrical work. Review (or bind Replace/Install scope on the object).",
          caution: null,
          scope: null,
          informational: false,
        });
      }
      continue; // present → Keep (or already scoped Replace via Phase 8) — never auto demolition
    }
    if (corrected.has(b.id)) {
      findings.push({
        key: `object-corrected:${root}`,
        entityKind: "object",
        classification: "review",
        autoApply: false,
        rootId: root,
        baselineIds: [b.id],
        proposedIds: [],
        quantityX100: 100,
        explanation: "This object was documented in Existing AFTER this proposed design was created. Confirm whether the proposal really removes it.",
        caution: null,
        scope: null,
        informational: false,
      });
      continue;
    }
    if (replacedByInstall.has(b.id)) {
      // §79.1: the matching installation pays to take this one out.
      findings.push({
        key: `object-removed:${root}`,
        entityKind: "object",
        classification: "removed",
        autoApply: false,
        rootId: root,
        baselineIds: [b.id],
        proposedIds: [replacedByInstall.get(b.id)!],
        quantityX100: 100,
        explanation:
          "This is being replaced — a new one of the same kind is in the proposed design, and taking the old one out is part of that installation, so it carries no separate demolition cost.",
        caution: null,
        scope: null,
        informational: true,
        supersededRemoval: true,
      });
      continue;
    }
    findings.push({
      key: `object-removed:${root}`,
      entityKind: "object",
      classification: "removed",
      autoApply: true,
      rootId: root,
      baselineIds: [b.id],
      proposedIds: [],
      quantityX100: 100,
      explanation: "This Existing object has no counterpart in the proposed design — it must be removed.",
      caution: null,
      scope: { featureDefinitionId: "remove-fixture", action: "remove", unit: "each" },
      informational: false,
    });
  }
  // Proposed-only objects: Phase 8 auto-scope territory — informational only
  // (the review list shows them as applied; NEVER re-scoped here).
  for (const p of propObjects) {
    const root = rootOf(p);
    if (baseline && [...baseObjects].some((b) => rootOf(b) === root)) continue;
    findings.push({
      key: `object-added:${p.id}`,
      entityKind: "object",
      classification: "added",
      autoApply: false,
      rootId: p.id,
      baselineIds: [],
      proposedIds: [p.id],
      quantityX100: 100,
      explanation: baseline
        ? "This object exists only in the proposed design; its Install scope is managed on the object itself."
        : "New construction object; its Install scope is managed on the object itself.",
      caution: null,
      scope: null,
      informational: true,
    });
  }

  return findings;
}
